/**
 * O botão de feedback: flutua no canto de baixo de toda tela do Sistema e abre
 * um painel para contar um problema, uma ideia ou um elogio — com prints.
 *
 * Três decisões que valem saber antes de mexer:
 *
 * **O rascunho sobrevive ao fechar.** O estado mora aqui, no botão, e não no
 * painel: fechar sem querer (Esc, clique no botão) não apaga o que foi escrito.
 * Só o envio bem-sucedido limpa.
 *
 * **A tela aberta vai junto.** "Deu erro" sem dizer onde é metade de um relato;
 * a rota atual segue no campo `pagina`, sem ocupar espaço no painel — quem lê o
 * feedback na aba "Equipe" é que a vê, como link para a tela.
 *
 * **Print entra de três jeitos**: escolher arquivo, arrastar, ou colar com
 * Ctrl+V — que é como quem tira print no Windows (Win+Shift+S) trabalha. E, onde
 * o navegador deixa, capturar a tela direto: o painel some durante a captura
 * para não sair na foto.
 *
 * **Ele vai para onde a pessoa arrastar.** O botão, ou o cabeçalho do painel,
 * arrasta os dois juntos; a posição fica guardada neste navegador, e um duplo
 * clique no cabeçalho devolve ao canto. Ver `posicaoDoFeedback.ts`.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  EVENTO_ABRIR_FEEDBACK, LIMITES, enviarFeedbackDoSistema, listarTiposDoSistema, segundosParaTentarDeNovo,
  type CodigoDoTipo, type FeedbackEnviado, type TipoDeFeedbackDoSistema,
} from "@/api/feedbackDoSistema"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { usarSessao } from "@/sessao/Sessao"
import {
  ImagemRecusada, capturarQuadro, podeCapturarTela, prepararImagem, quadroParaArquivo, tamanhoLegivel,
} from "./imagensDoFeedback"
import { D, Icone, IconeDoTipo } from "./iconesDoFeedback"
import {
  BOTAO_FECHADO as BOTAO, LIMIAR_DO_ARRASTO, POSICAO_PADRAO, ancoraDoBotao, gravarPosicao, lerPosicao,
  lugarDoPainel, mover, prender,
  type Posicao, type Tamanho,
} from "./posicaoDoFeedback"

/**
 * O catálogo que a tela usa enquanto a API não respondeu. É o mesmo da API
 * (`FeedbackDoSistemaService.Tipos`); o ícone, a cor e o texto de ajuda de cada
 * um são da tela e só existem aqui.
 */
const TIPOS_LOCAIS: TipoDeFeedbackDoSistema[] = [
  { codigo: "problema", nome: "Problema", descricao: "Algo quebrado, errado ou confuso" },
  { codigo: "ideia", nome: "Ideia", descricao: "Uma melhoria ou algo que falta" },
  { codigo: "elogio", nome: "Elogio", descricao: "Algo que ficou bom e deve continuar assim" },
]

const MOLDE: Record<CodigoDoTipo, string> = {
  problema: "O que aconteceu? O que você esperava que acontecesse? Se der, conte os passos para repetir.",
  ideia: "O que ajudaria no seu dia a dia? Um exemplo de uso deixa a ideia bem mais clara.",
  elogio: "O que ficou bom? Saber o que funciona ajuda a manter assim.",
}

type Anexo = { id: string; arquivo: File; url: string }

const CHAVE_PULSO = "sistema-oc.feedback-visto"

function tamanhoDaJanela(): Tamanho {
  return { largura: window.innerWidth, altura: window.innerHeight }
}

/**
 * Arrastar com o ponteiro (mouse, caneta ou dedo). Só vira arrasto depois de
 * {@link LIMIAR_DO_ARRASTO} px: abaixo disso é clique, e o clique segue normal.
 * `arrastou` diz ao `onClick` que veio de um arrasto e deve ser ignorado.
 */
function usarArrasto(posicao: Posicao, aoMover: (p: Posicao) => void, aoSoltar: (p: Posicao) => void) {
  const inicio = useRef<{ x: number; y: number; posicao: Posicao; arrastando: boolean } | null>(null)
  const arrastou = useRef(false)
  const [arrastando, setArrastando] = useState(false)

  const alvo = {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return
      // No cabeçalho, o botão de fechar continua sendo botão. Comparar com o próprio alvo
      // do arrasto, e não só procurar um botão: no botão de feedback o ponteiro cai no
      // ícone, e o botão mais próximo é ele mesmo.
      const outroBotao = (e.target as Element).closest("button")
      if (outroBotao && outroBotao !== e.currentTarget) return
      inicio.current = { x: e.clientX, y: e.clientY, posicao, arrastando: false }
      arrastou.current = false
      // Captura já no aperto: um puxão rápido tira o ponteiro de cima do botão no
      // primeiro movimento, e sem a captura esse movimento iria para outro elemento.
      e.currentTarget.setPointerCapture?.(e.pointerId)
    },
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
      const i = inicio.current
      if (!i) return
      const dx = e.clientX - i.x
      const dy = e.clientY - i.y
      if (!i.arrastando) {
        if (Math.hypot(dx, dy) < LIMIAR_DO_ARRASTO) return
        i.arrastando = true
        setArrastando(true)
      }
      aoMover(mover(i.posicao, dx, dy))
    },
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => {
      const i = inicio.current
      inicio.current = null
      if (!i?.arrastando) return
      arrastou.current = true
      setArrastando(false)
      aoSoltar(mover(i.posicao, e.clientX - i.x, e.clientY - i.y))
    },
    onPointerCancel: () => {
      if (inicio.current?.arrastando) setArrastando(false)
      inicio.current = null
    },
  }
  return { alvo, arrastou, arrastando }
}

export function BotaoDeFeedback() {
  const { nome } = usarSessao()
  const { pathname, search } = useLocation()
  const qc = useQueryClient()

  const [aberto, setAberto] = useState(false)
  const [capturando, setCapturando] = useState(false)
  const [tipo, setTipo] = useState<CodigoDoTipo | null>(null)
  const [texto, setTexto] = useState("")
  const [anexos, setAnexos] = useState<Anexo[]>([])
  const [aviso, setAviso] = useState<string | null>(null)
  const [esperar, setEsperar] = useState(0)
  const [enviado, setEnviado] = useState<FeedbackEnviado | null>(null)
  const [pulsar] = useState(() => {
    try {
      if (sessionStorage.getItem(CHAVE_PULSO)) return false
      sessionStorage.setItem(CHAVE_PULSO, "1")
      return true
    } catch {
      return false
    }
  })

  const botao = useRef<HTMLButtonElement>(null)
  const pagina = pathname + search

  const [janela, setJanela] = useState<Tamanho>(tamanhoDaJanela)
  const [posicao, setPosicao] = useState<Posicao>(lerPosicao)
  useEffect(() => {
    const medir = () => setJanela(tamanhoDaJanela())
    window.addEventListener("resize", medir)
    return () => window.removeEventListener("resize", medir)
  }, [])

  // Sempre dentro da janela — a posição salva pode ter vindo de uma tela maior.
  const noLugar = prender(posicao, janela, BOTAO)
  const painel = lugarDoPainel(noLugar, BOTAO, janela)
  const ancora = ancoraDoBotao(noLugar, janela)
  const arrasto = usarArrasto(
    noLugar,
    (p) => setPosicao(prender(p, janela, BOTAO)),
    (p) => { const solto = prender(p, janela, BOTAO); setPosicao(solto); gravarPosicao(solto) },
  )
  const voltarAoCanto = () => { setPosicao(POSICAO_PADRAO); gravarPosicao(null) }

  const tipos = useQuery({
    queryKey: ["feedback-sistema-tipos"] as const,
    queryFn: listarTiposDoSistema,
    staleTime: Infinity,
    enabled: aberto,
    placeholderData: TIPOS_LOCAIS,
  })

  // As URLs de miniatura são do navegador e vazam se ninguém as devolve.
  const anexosRef = useRef(anexos)
  anexosRef.current = anexos
  useEffect(() => () => anexosRef.current.forEach((a) => URL.revokeObjectURL(a.url)), [])

  // O 429 vira contagem regressiva no próprio botão de enviar.
  useEffect(() => {
    if (esperar <= 0) return
    const t = setTimeout(() => setEsperar((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [esperar])

  const enviar = useMutation({
    mutationFn: enviarFeedbackDoSistema,
    onSuccess: (r) => {
      anexos.forEach((a) => URL.revokeObjectURL(a.url))
      setAnexos([])
      setTexto("")
      setTipo(null)
      setAviso(null)
      setEnviado(r)
      qc.invalidateQueries({ queryKey: ["feedback-sistema"] })
    },
    onError: (e) => {
      const s = segundosParaTentarDeNovo(e)
      if (s) setEsperar(s)
      setAviso(
        e instanceof ErroDaApi || e instanceof ErroDeTrava
          ? e.message
          : "Não foi possível enviar. Confira a conexão e tente de novo — o texto continua aqui.",
      )
    },
  })

  const fechar = useCallback(() => {
    if (enviar.isPending) return
    setAberto(false)
    // Depois de enviado, reabrir começa do zero em vez de mostrar o "obrigado".
    setEnviado(null)
    requestAnimationFrame(() => botao.current?.focus())
  }, [enviar.isPending])

  // Outras telas abrem o botão — as Solicitações, com o que a pessoa procurou e não achou.
  // Rascunho já escrito não é trocado: o texto que vem só entra no campo vazio.
  useEffect(() => {
    const abrirDeFora = (e: Event) => {
      const pedido = (e as CustomEvent<{ tipo?: CodigoDoTipo; texto?: string }>).detail ?? {}
      setEnviado(null)
      if (pedido.tipo) setTipo(pedido.tipo)
      if (pedido.texto) setTexto((atual) => (atual.trim() ? atual : pedido.texto!))
      setAberto(true)
    }
    window.addEventListener(EVENTO_ABRIR_FEEDBACK, abrirDeFora)
    return () => window.removeEventListener(EVENTO_ABRIR_FEEDBACK, abrirDeFora)
  }, [])

  useEffect(() => {
    if (!aberto) return
    const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") fechar() }
    document.addEventListener("keydown", tecla)
    return () => document.removeEventListener("keydown", tecla)
  }, [aberto, fechar])

  /** `prontos`: já saíram no formato e tamanho finais (a captura de tela) e não passam de novo por `prepararImagem`. */
  const adicionar = useCallback(async (arquivos: File[], prontos = false) => {
    if (arquivos.length === 0) return
    const vagas = LIMITES.imagens - anexosRef.current.length
    if (vagas <= 0) {
      setAviso(`São no máximo ${LIMITES.imagens} imagens por feedback.`)
      return
    }

    const recusas: string[] = []
    const novos: Anexo[] = []
    for (const arquivo of arquivos.slice(0, vagas)) {
      try {
        const pronto = prontos ? arquivo : await prepararImagem(arquivo)
        novos.push({ id: crypto.randomUUID(), arquivo: pronto, url: URL.createObjectURL(pronto) })
      } catch (e) {
        recusas.push(e instanceof ImagemRecusada ? e.message : `Não consegui ler "${arquivo.name}".`)
      }
    }
    if (arquivos.length > vagas) recusas.push(`Só cabem ${LIMITES.imagens} imagens — as demais ficaram de fora.`)

    setAnexos((atuais) => [...atuais, ...novos])
    setAviso(recusas.length ? recusas.join(" ") : null)
  }, [])

  const remover = (id: string) => {
    setAnexos((atuais) => {
      const sai = atuais.find((a) => a.id === id)
      if (sai) URL.revokeObjectURL(sai.url)
      return atuais.filter((a) => a.id !== id)
    })
  }

  const capturar = async () => {
    setCapturando(true)
    try {
      const quadro = await capturarQuadro()
      // O painel volta assim que o quadro chegou; o JPEG é gerado com ele já à vista.
      setCapturando(false)
      await adicionar([await quadroParaArquivo(quadro)], true)
    } catch (e) {
      // Cancelar a escolha no navegador também cai aqui (NotAllowedError) — e não é erro.
      if (e instanceof ImagemRecusada) setAviso(e.message)
      else if (!(e instanceof DOMException && e.name === "NotAllowedError"))
        setAviso("O navegador não deixou capturar a tela. Tire um print e cole aqui com Ctrl+V.")
    } finally {
      setCapturando(false)
    }
  }

  const mensagem = texto.trim()
  const podeEnviar = !!tipo && mensagem.length > 0 && mensagem.length <= LIMITES.mensagem
    && !enviar.isPending && esperar <= 0

  const confirmar = () => {
    if (!podeEnviar || !tipo) return
    setAviso(null)
    enviar.mutate({ tipo, mensagem, pagina, imagens: anexos.map((a) => a.arquivo) })
  }

  return (
    <>
      {aberto && (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="fb-titulo"
          className={`fb-painel${arrasto.arrastando ? " fb-arrastando" : ""}`}
          style={{
            // Inline e não no CSS: a posição é da pessoa. `left: auto` desfaz o do celular.
            left: "auto", right: painel.direita, width: painel.largura,
            bottom: painel.baixo ?? "auto", top: painel.topo ?? "auto",
            maxHeight: painel.alturaMaxima, transformOrigin: painel.origem,
            visibility: capturando ? "hidden" : undefined,
          }}
          onPaste={(e) => {
            const fotos = [...e.clipboardData.files].filter((f) => f.type.startsWith("image/"))
            if (fotos.length === 0) return
            e.preventDefault()
            void adicionar(fotos)
          }}
        >
          <header
            className="fb-cabeca"
            title="Arraste para mover · duplo clique volta ao canto"
            {...arrasto.alvo}
            onDoubleClick={(e) => { if (!(e.target as Element).closest("button")) voltarAoCanto() }}
          >
            <h2 id="fb-titulo">{enviado ? "Feedback enviado" : "Envie seu feedback"}</h2>
            <p>Vai direto para quem desenvolve o Sistema. Toda mensagem é lida.</p>
            <button type="button" className="fb-fechar" onClick={fechar} aria-label="Fechar">
              <Icone d={D.fechar} tamanho={16} />
            </button>
          </header>

          {enviado ? (
            <Sucesso
              nome={nome}
              enviado={enviado}
              aoNovo={() => setEnviado(null)}
              aoFechar={fechar}
            />
          ) : (
            <>
              <div className="fb-corpo">
                <EscolhaDoTipo
                  tipos={tipos.data ?? TIPOS_LOCAIS}
                  valor={tipo}
                  aoEscolher={setTipo}
                />

                <CampoDeTexto
                  tipo={tipo}
                  valor={texto}
                  aoMudar={setTexto}
                  aoConfirmar={confirmar}
                />

                <Anexos
                  anexos={anexos}
                  aoAdicionar={adicionar}
                  aoRemover={remover}
                  aoCapturar={podeCapturarTela ? capturar : undefined}
                />

                {aviso && (
                  <div className="fb-aviso" role="alert">
                    <Icone d={D.alerta} tamanho={15} />
                    <span>{aviso}</span>
                  </div>
                )}
              </div>

              <footer className="fb-pe">
                <button type="button" className="fb-enviar" disabled={!podeEnviar} onClick={confirmar}>
                  {enviar.isPending ? (
                    <><span className="fb-giro" aria-hidden="true" /> Enviando…</>
                  ) : esperar > 0 ? (
                    <>Aguarde {esperar} s</>
                  ) : (
                    <>
                      <Icone d={D.enviar} tamanho={15} />
                      Enviar feedback
                    </>
                  )}
                </button>
              </footer>
            </>
          )}
        </div>
      )}

      <button
        ref={botao}
        type="button"
        className={`fb-fab${pulsar && !aberto ? " fb-fab-pulso" : ""}${arrasto.arrastando ? " fb-arrastando" : ""}`}
        aria-expanded={aberto}
        aria-haspopup="dialog"
        aria-label={aberto ? "Fechar o feedback" : "Enviar feedback sobre o Sistema"}
        title={aberto ? undefined : "Enviar feedback sobre o Sistema · arraste para mover"}
        {...arrasto.alvo}
        onClick={() => {
          // Soltar depois de arrastar também dispara clique; esse não abre nem fecha.
          if (arrasto.arrastou.current) { arrasto.arrastou.current = false; return }
          if (aberto) fechar()
          else setAberto(true)
        }}
        style={{
          // Preso pela direita cresce para a esquerda no hover; na borda esquerda, o contrário.
          left: ancora.left ?? "auto", right: ancora.right ?? "auto", bottom: ancora.bottom,
          visibility: capturando ? "hidden" : undefined,
        }}
      >
        <span className="fb-fab-icone">
          {aberto ? (
            <Icone d={D.fechar} tamanho={20} largura={2.2} />
          ) : (
            <Icone d={D.balao} tamanho={21} largura={1.9} />
          )}
        </span>
        <span className="fb-fab-rotulo">{aberto ? "Fechar" : "Feedback"}</span>
        {anexos.length + (texto.trim() ? 1 : 0) > 0 && !aberto && <Rascunho />}
      </button>
    </>
  )
}

// ------------------------------------------------------------------ partes

function EscolhaDoTipo({ tipos, valor, aoEscolher }: {
  tipos: TipoDeFeedbackDoSistema[]
  valor: CodigoDoTipo | null
  aoEscolher: (c: CodigoDoTipo) => void
}) {
  const primeiro = useRef<HTMLButtonElement>(null)
  // Abrir o painel já com o foco na primeira escolha: o teclado não precisa caçar.
  useEffect(() => { if (!valor) primeiro.current?.focus() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const escolhido = tipos.find((t) => t.codigo === valor)

  return (
    <div>
      <div className="fb-rotulo" id="fb-rotulo-tipo">Sobre o que é?</div>
      <div className="fb-tipos" role="radiogroup" aria-labelledby="fb-rotulo-tipo">
        {tipos.map((t, i) => (
          <button
            key={t.codigo}
            ref={i === 0 ? primeiro : undefined}
            type="button"
            role="radio"
            aria-checked={valor === t.codigo}
            className={`fb-tipo fb-${t.codigo}`}
            title={t.descricao}
            onClick={() => aoEscolher(t.codigo)}
          >
            {valor === t.codigo && (
              <span className="fb-tipo-marca" aria-hidden="true">
                <Icone d={D.check} tamanho={10} largura={3} />
              </span>
            )}
            <span className="fb-tipo-icone" aria-hidden="true"><IconeDoTipo tipo={t.codigo} /></span>
            <span className="fb-tipo-nome">{t.nome}</span>
          </button>
        ))}
      </div>
      <p className="fb-tipo-dica">{escolhido?.descricao ?? "Escolha um para começar."}</p>
    </div>
  )
}

function CampoDeTexto({ tipo, valor, aoMudar, aoConfirmar }: {
  tipo: CodigoDoTipo | null
  valor: string
  aoMudar: (v: string) => void
  aoConfirmar: () => void
}) {
  const id = useId()
  const campo = useRef<HTMLTextAreaElement>(null)
  const tamanho = valor.trim().length

  // Escolheu o tipo, o cursor vai para o texto — é a próxima coisa a fazer.
  useEffect(() => { if (tipo) campo.current?.focus() }, [tipo])

  return (
    <div>
      <label className="fb-rotulo" htmlFor={id}>Mensagem</label>
      <div className="fb-texto">
        <textarea
          id={id}
          ref={campo}
          value={valor}
          maxLength={LIMITES.mensagem + 200}
          placeholder={tipo ? MOLDE[tipo] : "Escolha o tipo acima e conte o que quiser."}
          onChange={(e) => aoMudar(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault()
              aoConfirmar()
            }
          }}
        />
        <span
          className="fb-contador"
          data-perto={tamanho > LIMITES.mensagem * 0.9}
          data-passou={tamanho > LIMITES.mensagem}
          aria-live="polite"
        >
          {tamanho}/{LIMITES.mensagem}
        </span>
      </div>
    </div>
  )
}

function Anexos({ anexos, aoAdicionar, aoRemover, aoCapturar }: {
  anexos: Anexo[]
  aoAdicionar: (arquivos: File[]) => void
  aoRemover: (id: string) => void
  aoCapturar?: () => void
}) {
  const entrada = useRef<HTMLInputElement>(null)
  const [arrastando, setArrastando] = useState(false)
  const cheio = anexos.length >= LIMITES.imagens
  const total = useMemo(() => anexos.reduce((s, a) => s + a.arquivo.size, 0), [anexos])

  return (
    <div>
      <div className="fb-rotulo">
        Prints e fotos
        <small>
          {anexos.length > 0
            ? `${anexos.length} de ${LIMITES.imagens} · ${tamanhoLegivel(total)}`
            : `opcional · até ${LIMITES.imagens}`}
        </small>
      </div>

      <button
        type="button"
        className="fb-soltar"
        data-arrastando={arrastando}
        disabled={cheio}
        onClick={() => entrada.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setArrastando(true) }}
        onDragLeave={() => setArrastando(false)}
        onDrop={(e) => {
          e.preventDefault()
          setArrastando(false)
          if (!cheio) aoAdicionar([...e.dataTransfer.files])
        }}
      >
        <span className="fb-soltar-icone" aria-hidden="true">
          <Icone d={D.imagem} tamanho={18} />
        </span>
        <span>
          <strong>{cheio ? "Limite de imagens atingido" : arrastando ? "Solte para anexar" : "Anexar imagens"}</strong>
          <span>Clique, arraste ou cole com <kbd>Ctrl</kbd>+<kbd>V</kbd> · JPG, PNG, WEBP</span>
        </span>
      </button>

      <input
        ref={entrada}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        hidden
        onChange={(e) => {
          aoAdicionar([...(e.target.files ?? [])])
          e.target.value = ""
        }}
      />

      {aoCapturar && (
        <div className="fb-acoes-anexo">
          <button type="button" className="fb-link" onClick={aoCapturar} disabled={cheio}>
            <Icone d={D.tela} tamanho={13} />
            Capturar a tela
          </button>
        </div>
      )}

      {anexos.length > 0 && (
        <div className="fb-miniaturas">
          {anexos.map((a) => (
            <div key={a.id} className="fb-miniatura" title={a.arquivo.name}>
              <img src={a.url} alt={a.arquivo.name} />
              <span className="fb-miniatura-peso">{tamanhoLegivel(a.arquivo.size)}</span>
              <button
                type="button"
                className="fb-miniatura-tirar"
                aria-label={`Remover ${a.arquivo.name}`}
                onClick={() => aoRemover(a.id)}
              >
                <Icone d={D.fechar} tamanho={11} largura={2.6} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Sucesso({ nome, enviado, aoNovo, aoFechar }: {
  nome: string
  enviado: FeedbackEnviado
  aoNovo: () => void
  aoFechar: () => void
}) {
  const primeiroNome = nome.trim().split(/\s+/)[0] ?? ""
  const fechar = useRef<HTMLButtonElement>(null)
  useEffect(() => { fechar.current?.focus() }, [])

  return (
    <div className="fb-sucesso" role="status">
      <span className="fb-selo" aria-hidden="true">
        <svg width="32" height="32" viewBox="0 0 24 24"
             style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round", strokeLinejoin: "round" }}>
          <path d="m5 12.5 4.5 4.5L19 7.5" />
        </svg>
      </span>
      <h3>Recebido{primeiroNome ? `, ${primeiroNome}` : ""}!</h3>
      <p>
        Obrigado por ajudar a melhorar o Sistema. Seu feedback{" "}
        <span className="mono">#{enviado.id}</span> chegou à equipe
        {enviado.quantidadeImagens > 0
          ? ` com ${enviado.quantidadeImagens} ${enviado.quantidadeImagens === 1 ? "imagem" : "imagens"}.`
          : "."}
      </p>
      <div className="fb-sucesso-botoes">
        <button type="button" className="fb-secundario" onClick={aoNovo}>Enviar outro</button>
        <button type="button" className="fb-enviar" ref={fechar} onClick={aoFechar}>Fechar</button>
      </div>
    </div>
  )
}

/** A bolinha no botão fechado: há rascunho esperando. */
function Rascunho() {
  return (
    <span
      title="Há um rascunho não enviado"
      style={{
        position: "absolute", top: 2, right: 2, width: 11, height: 11, borderRadius: "50%",
        background: "var(--brand)", border: "2px solid var(--surface)",
      }}
    />
  )
}
